import { AdminPaths, LogisticsPaths, PublicPaths, UserPaths } from '@/models/appRoutes';
import { Permission } from '@/models/Permission';
import { Role } from '@/models/role';
import { AuthService } from '@/services/auth.service';
import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-header',
  imports: [RouterLink, TranslatePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  authService = inject(AuthService);
  route = inject(Router);
  translate = inject(TranslateService);
  PublicPaths = PublicPaths;
  UserPaths = UserPaths;
  AdminPaths = AdminPaths;
  showUserMenu = signal(false);
  showSettingsMenu = signal(false);

  readonly adminMenuItems: ReadonlyArray<{
    label: string;
    routerLink: string;
    permission?: Permission;
    systemAdminOnly?: boolean;
  }> = [
    {
      label: 'headerNav.admin.registrationManagement',
      routerLink: AdminPaths.RegistrationManagement,
      permission: 'admin:registration-management',
    },
    {
      label: 'headerNav.admin.userManagement',
      routerLink: AdminPaths.UserManagement,
      permission: 'admin:user-management',
    },
    {
      label: 'headerNav.admin.rolesManagement',
      routerLink: AdminPaths.RolesManagement,
      permission: 'admin:roles-management',
    },
    {
      label: 'headerNav.admin.apiManagement',
      routerLink: AdminPaths.ApiKeyManagement,
      permission: 'admin:api-key-management',
    },
    {
      label: 'headerNav.admin.tenantManagement',
      routerLink: AdminPaths.TenantManagement,
      permission: 'admin:tenant-management',
    },
    {
      label: 'headerNav.admin.companyManagement',
      routerLink: AdminPaths.CompanyManagement,
      permission: 'admin:company-management',
    },
    {
      label: 'headerNav.admin.typeMappingManagement',
      routerLink: AdminPaths.TypeMappingManagement,
      permission: 'admin:type-mapping-management',
    },
    {
      label: 'headerNav.admin.assistTableManagement',
      routerLink: AdminPaths.AssistTables,
      systemAdminOnly: true,
    },
  ];

  visibleAdminMenuItems = computed(() => {
    const visibleItems = this.adminMenuItems.filter((item) => {
      const isSystemAdmin = this.authService.hasRoles([Role.SYSTEMADMIN]);
      const hasPermission = item.permission
        ? this.authService.hasPermissions([item.permission])
        : false;
      const isVisible = isSystemAdmin || hasPermission;

      return isVisible;
    });

    return visibleItems;
  });

  canAccessAdminMenu = computed(() => this.visibleAdminMenuItems().length > 0);

  readonly modules: ReadonlyArray<{
    labelKey: string;
    routerLink: string;
    permission: Permission;
  }> = [
    {
      labelKey: 'headerNav.shipments',
      routerLink: LogisticsPaths.Shipments,
      permission: 'logistics:shipments',
    },
  ];

  visibleModules = computed(() =>
    this.modules.filter((module) => this.authService.hasPermissions([module.permission])),
  );

  canAccessDashboard = computed(() => this.authService.hasPermissions(['user:dashboard']));

  ngOnInit() {}

  userRoleLabel() {
    const role = this.authService.user()?.roles?.[0];

    switch (role) {
      case Role.SYSTEMADMIN:
        return 'System Admin';
      case Role.SYSTEMSUPPORT:
        return 'System Support';
      default:
        return role ?? '';
    }
  }

  userName() {
    return this.authService.isLoggedIn().toString();
  }

  showMainNavbar() {
    const currentUrl = (this.route.url ?? '').toLowerCase();
    const loginPath = (PublicPaths.Login ?? '').toLowerCase();
    const isLoggedInOnLoginPage = this.authService.isLoggedIn() && currentUrl.startsWith(loginPath);

    return this.authService.isLoggedIn() && !isLoggedInOnLoginPage;
  }

  changeLanguage(lang: 'en' | 'he') {
    const current = (
      this.translate.currentLang ||
      this.translate.getFallbackLang() ||
      'en'
    ).toLowerCase();
    if (current.startsWith(lang)) {
      return;
    }

    this.translate.use(lang).subscribe();
  }

  isLanguageActive(lang: 'en' | 'he') {
    const current = (
      this.translate.currentLang ||
      this.translate.getFallbackLang() ||
      'en'
    ).toLowerCase();
    return current.startsWith(lang);
  }

  toggleUserMenu() {
    this.showUserMenu.update((value) => !value);
    this.showSettingsMenu.set(false);
  }

  toggleSettingsMenu() {
    this.showSettingsMenu.update((value) => !value);
    this.showUserMenu.set(false);
  }

  isAdminRouteActive(): boolean {
    const currentUrl = (this.route.url ?? '').toLowerCase();
    const adminRoot = (this.AdminPaths.Root ?? '').toLowerCase();
    return currentUrl.startsWith(adminRoot);
  }

  isRouteActive(routePath: string): boolean {
    const currentUrl = (this.route.url ?? '').toLowerCase();
    const targetPath = (routePath ?? '').toLowerCase();
    return currentUrl === targetPath || currentUrl.startsWith(`${targetPath}/`);
  }

  openCookieSettings() {
    window.dispatchEvent(new CustomEvent('open-cookie-settings'));
    this.closeMenus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target;

    if (target instanceof Element && target.closest('.nav-button, .user-button')) {
      return;
    }

    this.closeMenus();
  }

  closeMenus() {
    this.showUserMenu.set(false);
    this.showSettingsMenu.set(false);
  }

  logout() {
    this.authService.logout().subscribe((x) => {
      this.route.navigate([PublicPaths.Login]);
    });
  }
}
